import { getErrorMessage } from '../../utils/serviceUtils';
import {
  OnlineClientCommand,
  OnlineClientContext,
  OnlineServerMessage,
  OnlineTransportSession,
} from '../../types/transport';
import { supabase } from '../../utils/supabaseClient';
import { resolveOnlineTransportConfig } from '../../utils/onlineTransport';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';
import type PartySocket from 'partysocket';

type MessageHandler = (message: OnlineServerMessage) => void;
type StatusHandler = (status: string) => void;
const PARTYKIT_DEFAULT_PARTY = 'main';

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseServerMessage(raw: unknown): OnlineServerMessage | null {
  if (!isObjectRecord(raw) || typeof raw.type !== 'string') return null;
  return raw as unknown as OnlineServerMessage;
}

class BaseTransportSession implements OnlineTransportSession {
  protected readonly messageHandlers = new Set<MessageHandler>();
  protected readonly statusHandlers = new Set<StatusHandler>();
  protected lastStatus: string | null = null;

  protected emitMessage(message: OnlineServerMessage) {
    this.messageHandlers.forEach((handler) => {
      handler(message);
    });
  }

  protected emitStatus(status: string) {
    this.lastStatus = status;
    this.statusHandlers.forEach((handler) => {
      handler(status);
    });
  }

  async send(command: OnlineClientCommand): Promise<void> {
    void command;
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    if (this.lastStatus) {
      queueMicrotask(() => {
        if (!this.statusHandlers.has(handler) || !this.lastStatus) return;
        handler(this.lastStatus);
      });
    }
    return () => {
      this.statusHandlers.delete(handler);
    };
  }

  async disconnect(): Promise<void> {
    this.statusHandlers.clear();
    this.messageHandlers.clear();
  }
}

class PartyKitTransportSession extends BaseTransportSession {
  private readonly socket: PartySocket;
  private disposed = false;

  constructor(socket: PartySocket) {
    super();
    this.socket = socket;
    this.socket.addEventListener('open', this.handleOpen);
    this.socket.addEventListener('close', this.handleClose);
    this.socket.addEventListener('error', this.handleError);
    this.socket.addEventListener('message', this.handleMessage);
  }

  private handleOpen = () => {
    this.emitStatus('SUBSCRIBED');
    this.emitMessage({
      type: 'CONNECTED',
      transport: 'PARTYKIT',
      at: new Date().toISOString(),
    });
  };

  private handleClose = () => {
    if (this.disposed) return;
    this.emitStatus('CLOSED');
  };

  private handleError = () => {
    this.emitStatus('CHANNEL_ERROR');
  };

  private handleMessage = (event: MessageEvent) => {
    if (typeof event.data !== 'string') return;
    try {
      const parsed: unknown = JSON.parse(event.data);
      const message = parseServerMessage(parsed);
      if (!message) return;
      this.emitMessage(message);
    } catch (error) {
      if (isVerboseLoggingEnabled()) {
        logger.warn('PARTYKIT_MESSAGE_PARSE_FAILED', {
          message: (error as Error)?.message,
        });
      }
    }
  };

  async send(command: OnlineClientCommand): Promise<void> {
    if (this.disposed) return;
    this.socket.send(JSON.stringify(command));
  }

  async disconnect(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.socket.removeEventListener('open', this.handleOpen);
    this.socket.removeEventListener('close', this.handleClose);
    this.socket.removeEventListener('error', this.handleError);
    this.socket.removeEventListener('message', this.handleMessage);
    const readyState = this.socket.readyState;
    if (readyState === 0) {
      const closeAfterOpen = () => {
        this.socket.removeEventListener('open', closeAfterOpen);
        this.socket.close(1000, 'client-disconnect');
      };
      this.socket.addEventListener('open', closeAfterOpen);
    } else if (readyState === 1 || readyState === 2) {
      this.socket.close(1000, 'client-disconnect');
    }
    await super.disconnect();
  }
}

async function readAccessToken(): Promise<string | null> {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      logger.warn('PARTYKIT_AUTH_SESSION_READ_FAILED', {
        message: error.message,
      });
      return null;
    }
    return data.session?.access_token || null;
  } catch (error) {
    logger.warn('PARTYKIT_AUTH_SESSION_READ_EXCEPTION', {
      message: getErrorMessage(error),
    });
    return null;
  }
}

export interface OnlineTransport {
  partyKitHost: string;
  connect(gameId: string, client: OnlineClientContext): Promise<OnlineTransportSession>;
}

let cachedTransport: OnlineTransport | null = null;

export function createOnlineTransport(): OnlineTransport {
  if (cachedTransport) {
    return cachedTransport;
  }
  const { partyKitHost } = resolveOnlineTransportConfig();

  if (isVerboseLoggingEnabled()) {
    logger.info('ONLINE_TRANSPORT_SELECTED', {
      transport: 'PARTYKIT',
      partyKitHost,
    });
  }

  cachedTransport = {
    partyKitHost,
    async connect(gameId: string, client: OnlineClientContext): Promise<OnlineTransportSession> {
      const { default: PartySocketCtor } = await import('partysocket');
      const accessToken = await readAccessToken();
      const socket = new PartySocketCtor({
        host: partyKitHost,
        party: PARTYKIT_DEFAULT_PARTY,
        room: gameId,
        query: {
          playerId: client.playerId ?? undefined,
          playerName: client.playerName ?? undefined,
          role: client.role ?? undefined,
          authToken: accessToken ?? undefined,
        },
      });

      return new PartyKitTransportSession(socket);
    },
  };

  return cachedTransport;
}
